// Public booking page (batch 5d).
//
// Calendly-1:1 pattern:
//   Left column  → worker card (display name, blurb, duration, price)
//   Right column → 14-day date strip + time-slot grid + booking form modal
//
// Mounted by `app.ts` BEFORE auth: any path that matches /book/:slug (or
// /people/book/:slug behind the hub-core proxy) renders this page and returns
// — the Discord shell never loads.
//
// Unknown slugs: the backend returns the same shape as a valid worker with
// zero slots (anti-enumeration), so we just show the "no availability" state.
//
// Token states handled by query param:
//   ?view=<confirmation_token>   → show booking summary
//   ?cancel=<cancel_token>       → confirm + cancel booking
//   (none)                       → show the slot picker

import { API_BASE } from '../api';

interface PublicWorker {
  display_name: string;
  blurb: string;
  session_minutes: number;
  currency: string;
  price_cents: number;
  timezone: string;
}

interface PublicSlot { start: string; end: string; }

interface PublicBookingView {
  worker: PublicWorker;
  slots: PublicSlot[];
  from_date: string;
  through_date: string;
  pay_on_arrival_only: boolean;
}

interface PublicBookResp {
  booking_id: string;
  status: string;
  confirmation_token: string;
  cancel_token: string;
  payment_url: string | null;
  confirmation_url: string;
}

interface PublicBookingInfo {
  id: string;
  worker_name: string;
  client_name: string;
  client_email: string;
  scheduled_start: string;
  scheduled_end: string;
  timezone: string;
  status: string;
  payment_status: string;
  amount_cents: number;
  currency: string;
  cancel_token: string;
  cancel_deadline: string | null;
}

interface ApiResponse<T> { status: 'ok' | 'error'; data?: T; error?: string; }

async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { credentials: 'omit' });
  const json = (await res.json()) as ApiResponse<T>;
  if (json.status === 'error') throw new Error(json.error || 'request failed');
  return json.data as T;
}

async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'omit',
  });
  const json = (await res.json()) as ApiResponse<T>;
  if (json.status === 'error') throw new Error(json.error || 'request failed');
  return json.data as T;
}

function esc(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}

function escAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}

function formatPrice(cents: number, currency: string): string {
  if (cents <= 0) return 'Free';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

// Parse "YYYY-MM-DDTHH:MM:SS" as a naive datetime — render as-is in worker tz.
function parseNaive(s: string): Date {
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/);
  if (!m) return new Date(s);
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6]));
}

function formatTime(iso: string): string {
  const d = parseNaive(iso);
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function formatFullDateTime(iso: string, tz: string): string {
  const d = parseNaive(iso);
  const datePart = d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const timePart = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${datePart} at ${timePart} (${tz})`;
}

function dayKey(iso: string): string {
  return iso.slice(0, 10);
}

// ICS generator — builds a VCALENDAR string for "Add to Apple/Outlook calendar".
function buildIcs(summary: string, start: string, end: string, description: string): string {
  const toIcsDate = (s: string): string => s.replace(/[-:]/g, '').slice(0, 15); // YYYYMMDDTHHMMSS
  const uid = `hub-booking-${Date.now()}@the-hub-ecosystems.com`;
  const now = toIcsDate(new Date().toISOString().replace('Z', '').slice(0, 19));
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Hub People//Booking//EN',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${now}`,
    `DTSTART:${toIcsDate(start)}`,
    `DTEND:${toIcsDate(end)}`,
    `SUMMARY:${summary.replace(/[,;]/g, '\\$&')}`,
    `DESCRIPTION:${description.replace(/[,;\n]/g, (c) => c === '\n' ? '\\n' : `\\${c}`)}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
}

function buildGoogleCalLink(summary: string, start: string, end: string, description: string): string {
  const toGcal = (s: string): string => s.replace(/[-:]/g, '').slice(0, 15);
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: summary,
    dates: `${toGcal(start)}/${toGcal(end)}`,
    details: description,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function downloadIcs(filename: string, contents: string): void {
  const blob = new Blob([contents], { type: 'text/calendar' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ──────────────────────────────────────────────────────────────────────────
// Rendering
// ──────────────────────────────────────────────────────────────────────────

function extractSlugFromPath(): string | null {
  const m = window.location.pathname.match(/\/book\/([^/?#]+)/);
  const slug = m && m[1];
  return slug ? slug.toLowerCase() : null;
}

function shell(title: string): HTMLElement {
  document.title = title;
  const root = (document.getElementById('app') ?? document.body) as HTMLElement;
  root.innerHTML = '';
  const page = document.createElement('div');
  page.className = 'bp-page';
  root.appendChild(page);
  return page;
}

function workerCardHtml(w: PublicWorker): string {
  const initial = (w.display_name || '?').charAt(0).toUpperCase();
  return `
    <div class="bp-card bp-worker">
      <div class="bp-worker-avatar">${esc(initial)}</div>
      <h1 class="bp-worker-name">${esc(w.display_name)}</h1>
      <div class="bp-worker-meta">
        <div class="bp-meta-row"><span class="bp-meta-icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></span><span>${w.session_minutes} min</span></div>
        <div class="bp-meta-row"><span class="bp-meta-icon">$</span><span>${esc(formatPrice(w.price_cents, w.currency))}</span></div>
        <div class="bp-meta-row"><span class="bp-meta-icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg></span><span>${esc(w.timezone)}</span></div>
      </div>
      ${w.blurb ? `<p class="bp-worker-blurb">${esc(w.blurb)}</p>` : ''}
    </div>`;
}

function injectStyles(): void {
  if (document.getElementById('bp-styles')) return;
  const style = document.createElement('style');
  style.id = 'bp-styles';
  style.textContent = `
    .bp-page { min-height: 100dvh; background: var(--surface-0, #1e1f22); color: var(--text-primary, #f2f3f5); padding: 40px 20px; font-family: system-ui, -apple-system, Segoe UI, sans-serif; box-sizing: border-box; }
    .bp-shell { max-width: 960px; margin: 0 auto; display: grid; grid-template-columns: 320px 1fr; gap: 24px; }
    @media (max-width: 720px) { .bp-shell { grid-template-columns: 1fr; } }
    .bp-card { background: var(--surface-1, #2b2d31); border: 1px solid var(--border-subtle, #3f4147); border-radius: var(--radius-lg, 12px); padding: 24px; }
    .bp-worker { text-align: center; position: sticky; top: 24px; }
    .bp-worker-avatar { width: 72px; height: 72px; border-radius: 50%; background: var(--accent, #16a34a); color: var(--text-inverse, #111); font-size: 28px; font-weight: 600; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; }
    .bp-worker-name { font-size: 24px; font-weight: 600; margin: 0 0 12px; color: var(--text-primary, #f2f3f5); }
    .bp-worker-meta { display: flex; flex-direction: column; gap: 8px; align-items: center; margin-bottom: 16px; color: var(--text-secondary, #b5bac1); font-size: 14px; }
    .bp-meta-row { display: flex; gap: 8px; align-items: center; }
    .bp-meta-icon { opacity: 0.7; width: 16px; display: inline-block; text-align: center; }
    .bp-worker-blurb { color: var(--text-secondary, #b5bac1); font-size: 14px; line-height: 1.5; text-align: left; margin: 0; padding-top: 16px; border-top: 1px solid var(--border-subtle, #3f4147); }
    .bp-picker-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
    .bp-picker-title { font-size: 18px; font-weight: 600; margin: 0; }
    .bp-nav-btn { background: transparent; border: 1px solid var(--border-subtle, #3f4147); color: var(--text-primary, #f2f3f5); border-radius: var(--radius-sm, 4px); padding: 6px 10px; cursor: pointer; font-size: 13px; }
    .bp-nav-btn:hover:not(:disabled) { background: var(--surface-2, #313338); }
    .bp-nav-btn:disabled { opacity: 0.4; cursor: not-allowed; }
    .bp-day-strip { display: flex; gap: 6px; overflow-x: auto; padding-bottom: 8px; margin-bottom: 20px; scrollbar-width: thin; }
    .bp-day-btn { flex: 0 0 auto; background: var(--surface-2, #313338); border: 1px solid var(--border-subtle, #3f4147); color: var(--text-secondary, #b5bac1); border-radius: var(--radius-md, 8px); padding: 10px 14px; cursor: pointer; font-size: 12px; min-width: 64px; text-align: center; }
    .bp-day-btn:hover { background: var(--surface-2-hover, #3a3c42); }
    .bp-day-btn.bp-day-active { background: var(--accent, #16a34a); color: var(--text-inverse, #111); border-color: var(--accent, #16a34a); }
    .bp-day-btn.bp-day-empty { opacity: 0.45; cursor: not-allowed; }
    .bp-day-name { display: block; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; }
    .bp-day-num { display: block; font-size: 18px; font-weight: 600; margin-top: 2px; color: var(--text-primary, #f2f3f5); }
    .bp-day-btn.bp-day-active .bp-day-num { color: var(--text-inverse, #111); }
    .bp-slot-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); gap: 8px; }
    .bp-slot-btn { background: var(--surface-2, #313338); border: 1px solid var(--border-subtle, #3f4147); color: var(--text-primary, #f2f3f5); border-radius: var(--radius-sm, 4px); padding: 12px; cursor: pointer; font-size: 14px; font-weight: 500; transition: all 0.12s; }
    .bp-slot-btn:hover { background: var(--accent-soft, rgba(22,163,74,0.15)); border-color: var(--accent, #16a34a); color: var(--accent, #16a34a); }
    .bp-empty { color: var(--text-muted, #949ba4); padding: 40px; text-align: center; font-size: 14px; }
    .bp-modal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; padding: 20px; z-index: 1000; }
    .bp-modal { background: var(--surface-1, #2b2d31); border-radius: var(--radius-lg, 12px); padding: 24px; max-width: 440px; width: 100%; max-height: 90dvh; overflow-y: auto; }
    .bp-modal-title { font-size: 18px; font-weight: 600; margin: 0 0 4px; }
    .bp-modal-sub { color: var(--text-secondary, #b5bac1); font-size: 14px; margin: 0 0 20px; }
    .bp-field { margin-bottom: 16px; }
    .bp-label { display: block; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em; color: var(--text-secondary, #b5bac1); margin-bottom: 6px; }
    .bp-input { width: 100%; background: var(--surface-0, #1e1f22); border: 1px solid var(--border-subtle, #3f4147); color: var(--text-primary, #f2f3f5); padding: 10px 12px; border-radius: var(--radius-sm, 4px); font-size: 14px; font-family: inherit; box-sizing: border-box; }
    .bp-input:focus { outline: none; border-color: var(--accent, #16a34a); }
    textarea.bp-input { resize: vertical; min-height: 72px; }
    .bp-btn { padding: 10px 16px; border-radius: var(--radius-sm, 4px); border: none; font-size: 14px; font-weight: 600; cursor: pointer; }
    .bp-btn-primary { background: var(--accent, #16a34a); color: var(--text-inverse, #111); }
    .bp-btn-primary:hover:not(:disabled) { background: var(--accent-hover, #15803d); }
    .bp-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
    .bp-btn-ghost { background: transparent; color: var(--text-secondary, #b5bac1); border: 1px solid var(--border-subtle, #3f4147); }
    .bp-btn-danger { background: var(--danger, #dc2626); color: #fff; }
    .bp-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 16px; }
    .bp-err { color: var(--danger, #dc2626); font-size: 13px; margin-top: 8px; min-height: 18px; }
    .bp-ok { color: var(--accent, #16a34a); font-size: 14px; font-weight: 600; }
    .bp-summary { text-align: center; padding: 24px 0; }
    .bp-summary-check { width: 56px; height: 56px; border-radius: 50%; background: var(--accent-soft, rgba(22,163,74,0.15)); color: var(--accent, #16a34a); display: flex; align-items: center; justify-content: center; font-size: 28px; margin: 0 auto 12px; }
    .bp-summary-title { font-size: 20px; font-weight: 600; margin: 0 0 6px; }
    .bp-summary-time { font-size: 16px; color: var(--text-secondary, #b5bac1); margin: 0 0 20px; }
    .bp-cal-links { display: flex; gap: 8px; justify-content: center; flex-wrap: wrap; margin-top: 20px; }
    .bp-cal-links a { display: inline-flex; gap: 6px; align-items: center; background: var(--surface-2, #313338); color: var(--text-primary, #f2f3f5); padding: 8px 12px; border-radius: var(--radius-sm, 4px); text-decoration: none; font-size: 13px; }
    .bp-cal-links a:hover { background: var(--surface-2-hover, #3a3c42); }
    .bp-notice { background: var(--info-soft, rgba(8,145,178,0.15)); color: var(--text-primary, #f2f3f5); border-left: 3px solid var(--info, #0891b2); padding: 10px 14px; border-radius: var(--radius-sm, 4px); font-size: 13px; margin-bottom: 16px; }
  `;
  document.head.appendChild(style);
}

// ──────────────────────────────────────────────────────────────────────────
// State: slot picker
// ──────────────────────────────────────────────────────────────────────────

interface PickerState {
  slug: string;
  view: PublicBookingView;
  selectedDay: string;
}

function groupSlotsByDay(slots: PublicSlot[]): Map<string, PublicSlot[]> {
  const map = new Map<string, PublicSlot[]>();
  for (const s of slots) {
    const k = dayKey(s.start);
    const arr = map.get(k) || [];
    arr.push(s);
    map.set(k, arr);
  }
  return map;
}

function listDaysFromRange(fromIso: string, days: number): string[] {
  const parts = fromIso.split('-').map(Number);
  const y = parts[0] || 0;
  const m = parts[1] || 1;
  const d = parts[2] || 1;
  const base = new Date(y, m - 1, d);
  const out: string[] = [];
  for (let i = 0; i < days; i++) {
    const dt = new Date(base.getFullYear(), base.getMonth(), base.getDate() + i);
    const iso = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
    out.push(iso);
  }
  return out;
}

function renderPicker(container: HTMLElement, st: PickerState): void {
  const byDay = groupSlotsByDay(st.view.slots);
  const days = listDaysFromRange(st.view.from_date, 14);
  const strip = days.map(d => {
    const daySlots = byDay.get(d) || [];
    const parts = d.split('-').map(Number);
    const dt = new Date(parts[0] || 0, (parts[1] || 1) - 1, parts[2] || 1);
    const dayName = dt.toLocaleDateString(undefined, { weekday: 'short' });
    const dayNum = dt.getDate();
    const activeCls = d === st.selectedDay ? ' bp-day-active' : '';
    const emptyCls = daySlots.length === 0 ? ' bp-day-empty' : '';
    return `<button class="bp-day-btn${activeCls}${emptyCls}" data-day="${escAttr(d)}" ${daySlots.length === 0 ? 'disabled' : ''}>
      <span class="bp-day-name">${esc(dayName)}</span>
      <span class="bp-day-num">${dayNum}</span>
    </button>`;
  }).join('');

  const daySlots = byDay.get(st.selectedDay) || [];
  const slotsHtml = daySlots.length === 0
    ? `<div class="bp-empty">No times available${st.view.slots.length === 0 ? '.' : ' for this day.'}</div>`
    : `<div class="bp-slot-grid">${daySlots.map(s =>
        `<button class="bp-slot-btn" data-start="${escAttr(s.start)}" data-end="${escAttr(s.end)}">${esc(formatTime(s.start))}</button>`
      ).join('')}</div>`;

  const notice = st.view.pay_on_arrival_only && st.view.worker.price_cents > 0
    ? `<div class="bp-notice">Online payment is not set up yet — you'll arrange payment with ${esc(st.view.worker.display_name)} on the day.</div>`
    : '';

  container.innerHTML = `
    <div class="bp-shell">
      ${workerCardHtml(st.view.worker)}
      <div class="bp-card">
        ${notice}
        <div class="bp-picker-header">
          <h2 class="bp-picker-title">Pick a time</h2>
        </div>
        <div class="bp-day-strip">${strip}</div>
        ${slotsHtml}
      </div>
    </div>`;

  container.querySelectorAll<HTMLButtonElement>('.bp-day-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const d = btn.dataset['day'];
      if (!d) return;
      st.selectedDay = d;
      renderPicker(container, st);
    });
  });

  container.querySelectorAll<HTMLButtonElement>('.bp-slot-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const start = btn.dataset['start'] || '';
      const end = btn.dataset['end'] || '';
      openBookModal(container, st, start, end);
    });
  });
}

function openBookModal(container: HTMLElement, st: PickerState, start: string, end: string): void {
  const backdrop = document.createElement('div');
  backdrop.className = 'bp-modal-backdrop';
  backdrop.innerHTML = `
    <div class="bp-modal" role="dialog" aria-modal="true" aria-label="Confirm booking">
      <h3 class="bp-modal-title">Confirm your booking</h3>
      <p class="bp-modal-sub">${esc(formatFullDateTime(start, st.view.worker.timezone))}</p>
      <div class="bp-field">
        <label class="bp-label" for="bp-name">Your name</label>
        <input class="bp-input" id="bp-name" maxlength="120" autocomplete="name" required>
      </div>
      <div class="bp-field">
        <label class="bp-label" for="bp-email">Email</label>
        <input class="bp-input" id="bp-email" type="email" maxlength="200" autocomplete="email" required>
      </div>
      <div class="bp-field">
        <label class="bp-label" for="bp-phone">Phone <span style="opacity:0.6;font-weight:400">(optional)</span></label>
        <input class="bp-input" id="bp-phone" type="tel" maxlength="40" autocomplete="tel">
      </div>
      <div class="bp-field">
        <label class="bp-label" for="bp-notes">Notes <span style="opacity:0.6;font-weight:400">(optional)</span></label>
        <textarea class="bp-input" id="bp-notes" maxlength="1000" rows="3"></textarea>
      </div>
      <div class="bp-err" id="bp-err"></div>
      <div class="bp-actions">
        <button class="bp-btn bp-btn-ghost" id="bp-cancel" type="button">Cancel</button>
        <button class="bp-btn bp-btn-primary" id="bp-confirm" type="button">Confirm booking</button>
      </div>
    </div>`;
  document.body.appendChild(backdrop);

  const close = (): void => { backdrop.remove(); };
  backdrop.addEventListener('click', (e) => { if (e.target === backdrop) close(); });
  document.getElementById('bp-cancel')?.addEventListener('click', close);

  const nameEl = document.getElementById('bp-name') as HTMLInputElement;
  nameEl.focus();

  document.getElementById('bp-confirm')?.addEventListener('click', async () => {
    const name = nameEl.value.trim();
    const email = (document.getElementById('bp-email') as HTMLInputElement).value.trim();
    const phone = (document.getElementById('bp-phone') as HTMLInputElement).value.trim();
    const notes = (document.getElementById('bp-notes') as HTMLTextAreaElement).value.trim();
    const errEl = document.getElementById('bp-err') as HTMLElement;
    errEl.textContent = '';
    if (!name) { errEl.textContent = 'Name is required'; return; }
    if (!email || !email.includes('@')) { errEl.textContent = 'A valid email is required'; return; }
    const confirmBtn = document.getElementById('bp-confirm') as HTMLButtonElement;
    confirmBtn.disabled = true; confirmBtn.textContent = 'Booking...';
    try {
      // Mitchell ask 2026-04-26: send the visitor's local timezone with the
      // booking submit so the confirmation email can render times in the
      // CLIENT's tz (not just the worker's). Backend stores client_timezone
      // alongside scheduled_start.
      let clientTimezone = '';
      try { clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { /* fall back to empty — server defaults to worker tz */ }
      const resp = await apiPost<PublicBookResp>(`/api/public/booking/${encodeURIComponent(st.slug)}/book`, {
        client_name: name,
        client_email: email,
        client_phone: phone,
        client_notes: notes,
        scheduled_start: start,
        scheduled_end: end,
        client_timezone: clientTimezone,
      });
      close();
      if (resp.payment_url) {
        window.location.href = resp.payment_url;
        return;
      }
      renderConfirmation(container, st.view.worker, {
        scheduled_start: start,
        scheduled_end: end,
        worker_name: st.view.worker.display_name,
        client_email: email,
        cancel_token: resp.cancel_token,
        confirmation_token: resp.confirmation_token,
        timezone: st.view.worker.timezone,
      });
    } catch (e) {
      errEl.textContent = String((e as Error).message || e);
      confirmBtn.disabled = false; confirmBtn.textContent = 'Confirm booking';
    }
  });
}

interface ConfirmCtx {
  scheduled_start: string;
  scheduled_end: string;
  worker_name: string;
  client_email: string;
  cancel_token: string;
  confirmation_token: string;
  timezone: string;
}

function renderConfirmation(container: HTMLElement, worker: PublicWorker, ctx: ConfirmCtx): void {
  const when = formatFullDateTime(ctx.scheduled_start, ctx.timezone);
  const summary = `Session with ${ctx.worker_name}`;
  const description = `Booking confirmed via The Hub Ecosystems. A confirmation email was sent to ${ctx.client_email}.`;
  const gcal = buildGoogleCalLink(summary, ctx.scheduled_start, ctx.scheduled_end, description);
  const cancelUrl = `${window.location.pathname}?cancel=${encodeURIComponent(ctx.cancel_token)}`;

  container.innerHTML = `
    <div class="bp-shell">
      ${workerCardHtml(worker)}
      <div class="bp-card">
        <div class="bp-summary">
          <div class="bp-summary-check">✓</div>
          <h2 class="bp-summary-title">You're booked</h2>
          <p class="bp-summary-time">${esc(when)}</p>
          <p style="color:var(--text-secondary, #b5bac1); font-size: 14px; margin: 0">A confirmation was sent to <strong>${esc(ctx.client_email)}</strong>.</p>
          <div class="bp-cal-links">
            <a href="${esc(gcal)}" target="_blank" rel="noopener noreferrer">Add to Google Calendar</a>
            <a href="#" id="bp-ics-download">Download .ics</a>
            <a href="${esc(cancelUrl)}">Cancel</a>
          </div>
        </div>
      </div>
    </div>`;

  document.getElementById('bp-ics-download')?.addEventListener('click', (e) => {
    e.preventDefault();
    const ics = buildIcs(summary, ctx.scheduled_start, ctx.scheduled_end, description);
    downloadIcs('booking.ics', ics);
  });
}

// ──────────────────────────────────────────────────────────────────────────
// View-by-token state (?view=CONF_TOKEN)
// ──────────────────────────────────────────────────────────────────────────

async function renderViewByToken(container: HTMLElement, token: string): Promise<void> {
  try {
    const info = await apiGet<PublicBookingInfo>(`/api/public/booking/view/${encodeURIComponent(token)}`);
    const worker: PublicWorker = {
      display_name: info.worker_name,
      blurb: '',
      session_minutes: 0,
      currency: info.currency,
      price_cents: info.amount_cents,
      timezone: info.timezone,
    };
    const when = formatFullDateTime(info.scheduled_start, info.timezone);
    const status = info.status.toUpperCase();
    const summary = `Session with ${info.worker_name}`;
    const description = `Booking via The Hub Ecosystems.`;
    const gcal = buildGoogleCalLink(summary, info.scheduled_start, info.scheduled_end, description);
    const cancelUrl = `${window.location.pathname}?cancel=${encodeURIComponent(info.cancel_token)}`;
    const cancelable = info.status !== 'cancelled' && info.status !== 'completed';

    container.innerHTML = `
      <div class="bp-shell">
        ${workerCardHtml(worker)}
        <div class="bp-card">
          <div class="bp-summary">
            <h2 class="bp-summary-title">Your booking</h2>
            <p class="bp-summary-time">${esc(when)}</p>
            <p style="color:var(--text-secondary, #b5bac1); font-size: 13px;">Status: <strong>${esc(status)}</strong> · Payment: <strong>${esc(info.payment_status.toUpperCase())}</strong></p>
            ${info.cancel_deadline ? `<p style="color:var(--text-muted, #949ba4); font-size: 12px; margin-top:8px">Cancel before ${esc(formatFullDateTime(info.cancel_deadline, info.timezone))}</p>` : ''}
            <div class="bp-cal-links">
              <a href="${esc(gcal)}" target="_blank" rel="noopener noreferrer">Add to Google Calendar</a>
              <a href="#" id="bp-ics-download">Download .ics</a>
              ${cancelable ? `<a href="${esc(cancelUrl)}">Cancel booking</a>` : ''}
            </div>
          </div>
        </div>
      </div>`;

    document.getElementById('bp-ics-download')?.addEventListener('click', (e) => {
      e.preventDefault();
      const ics = buildIcs(summary, info.scheduled_start, info.scheduled_end, description);
      downloadIcs('booking.ics', ics);
    });
  } catch (e) {
    container.innerHTML = `
      <div class="bp-shell" style="grid-template-columns: 1fr">
        <div class="bp-card">
          <h2 class="bp-picker-title">Booking not found</h2>
          <p style="color: var(--text-secondary, #b5bac1); margin-top: 12px">${esc(String((e as Error).message || e))}</p>
        </div>
      </div>`;
  }
}

// ──────────────────────────────────────────────────────────────────────────
// Cancel-by-token state (?cancel=CANCEL_TOKEN)
// ──────────────────────────────────────────────────────────────────────────

async function renderCancelByToken(container: HTMLElement, token: string): Promise<void> {
  container.innerHTML = `
    <div class="bp-shell" style="grid-template-columns: 1fr">
      <div class="bp-card">
        <h2 class="bp-picker-title">Cancel booking</h2>
        <p style="color: var(--text-secondary, #b5bac1); margin: 12px 0 20px">Are you sure you want to cancel this booking?</p>
        <div class="bp-err" id="bp-err"></div>
        <div class="bp-actions">
          <a href="${esc(window.location.pathname)}" class="bp-btn bp-btn-ghost" style="text-decoration:none;display:inline-block">Keep booking</a>
          <button class="bp-btn bp-btn-danger" id="bp-do-cancel">Cancel booking</button>
        </div>
      </div>
    </div>`;
  document.getElementById('bp-do-cancel')?.addEventListener('click', async () => {
    const errEl = document.getElementById('bp-err') as HTMLElement;
    errEl.textContent = '';
    const btn = document.getElementById('bp-do-cancel') as HTMLButtonElement;
    btn.disabled = true; btn.textContent = 'Cancelling...';
    try {
      await apiPost<unknown>(`/api/public/booking/cancel/${encodeURIComponent(token)}`, {});
      container.innerHTML = `
        <div class="bp-shell" style="grid-template-columns: 1fr">
          <div class="bp-card">
            <div class="bp-summary">
              <div class="bp-summary-check">✓</div>
              <h2 class="bp-summary-title">Booking cancelled</h2>
              <p class="bp-summary-time">We've let the host know.</p>
            </div>
          </div>
        </div>`;
    } catch (e) {
      errEl.textContent = String((e as Error).message || e);
      btn.disabled = false; btn.textContent = 'Cancel booking';
    }
  });
}

// ──────────────────────────────────────────────────────────────────────────
// Entry
// ──────────────────────────────────────────────────────────────────────────

export async function renderBookingPublic(): Promise<void> {
  injectStyles();
  const slug = extractSlugFromPath();
  if (!slug) {
    shell('Booking').innerHTML = '<div class="bp-empty">Invalid booking link.</div>';
    return;
  }

  const params = new URLSearchParams(window.location.search);
  const viewTok = params.get('view');
  const cancelTok = params.get('cancel');
  const page = shell('Book a session');

  if (cancelTok) { await renderCancelByToken(page, cancelTok); return; }
  if (viewTok) { await renderViewByToken(page, viewTok); return; }

  try {
    const view = await apiGet<PublicBookingView>(`/api/public/booking/${encodeURIComponent(slug)}`);
    const byDay = groupSlotsByDay(view.slots);
    const firstDay = listDaysFromRange(view.from_date, 14).find(d => (byDay.get(d) || []).length > 0) || view.from_date;
    document.title = `Book with ${view.worker.display_name}`;
    renderPicker(page, { slug, view, selectedDay: firstDay });
  } catch (e) {
    page.innerHTML = `<div class="bp-empty">${esc(String((e as Error).message || e))}</div>`;
  }
}

/** Returns true if the current path is a public booking page and we should
 *  mount the booking UI BEFORE auth. Call from app.ts init(). */
export function isPublicBookingPath(): boolean {
  return /\/book\/[^/]+/.test(window.location.pathname);
}
